import { useState, useCallback, useMemo } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { Task } from '../types';

export function useTaskManager() {
  const [tasks, setTasks] = useLocalStorage<Task[]>('quantum-tasks', []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoized computed values for better performance
  const completedTasks = useMemo(() => 
    tasks.filter(task => task.completed), [tasks]
  );
  
  const pendingTasks = useMemo(() => 
    tasks.filter(task => !task.completed), [tasks]
  );
  
  const totalTasks = useMemo(() => tasks.length, [tasks]);
  const completionRate = useMemo(() => 
    totalTasks > 0 ? (completedTasks.length / totalTasks) * 100 : 0, 
    [completedTasks.length, totalTasks]
  );

  const addTask = useCallback(async (text: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Easter egg: typing "42" completes everything
      if (text === "42") {
        setTasks(prev => prev.map(task => ({ ...task, completed: true })));
        return;
      }

      const newTask: Task = {
        id: Date.now() + Math.random(), // Better unique ID generation
        text: text.trim(),
        completed: false,
        lifeMeaningScore: Math.floor(Math.random() * 1001),
        quantumState: 'superposition',
        excuses: [],
        createdAt: new Date()
      };

      setTasks(prev => [newTask, ...prev]); // Add to beginning for better UX
    } catch (err) {
      setError('Failed to add task. Please try again.');
      console.error('Error adding task:', err);
    } finally {
      setIsLoading(false);
    }
  }, [setTasks]);

  const toggleTask = useCallback(async (id: number) => {
    try {
      setError(null);
      setTasks(prev => prev.map(task => 
        task.id === id 
          ? { ...task, completed: !task.completed }
          : task
      ));
    } catch (err) {
      setError('Failed to update task. Please try again.');
      console.error('Error toggling task:', err);
    }
  }, [setTasks]);

  const deleteTask = useCallback(async (id: number) => {
    try {
      setError(null);
      setTasks(prev => prev.filter(task => task.id !== id));
    } catch (err) {
      setError('Failed to delete task. Please try again.');
      console.error('Error deleting task:', err);
    }
  }, [setTasks]);

  const generateExcuse = useCallback(async (task: Task) => {
    try {
      setError(null);
      const excuses = [
        "My WiFi collapsed into a quantum state.",
        "The Universe refused permission.",
        "Debugging karma points were too low.",
        "The code was written in a language I don't speak.",
        "My cat sat on the keyboard and created a paradox.",
        "The laws of physics prevented completion.",
        "I was abducted by aliens (they needed help with their React app).",
        "The task became self-aware and refused to be completed.",
        "My motivation was in a different timezone.",
        "The universe needed a break from my productivity.",
        "The quantum fluctuations in my coffee cup disrupted my focus.",
        "My brain's cache got corrupted by cosmic radiation.",
        "The task entered a parallel dimension where it's already completed.",
        "My productivity was affected by gravitational waves.",
        "The task's wave function collapsed into procrastination mode."
      ];
      
      const randomExcuse = excuses[Math.floor(Math.random() * excuses.length)];
      setTasks(prev => prev.map(t => 
        t.id === task.id 
          ? { ...t, excuses: [...t.excuses, randomExcuse] }
          : t
      ));
    } catch (err) {
      setError('Failed to generate excuse. Please try again.');
      console.error('Error generating excuse:', err);
    }
  }, [setTasks]);

  const clearCompletedTasks = useCallback(async () => {
    try {
      setError(null);
      setTasks(prev => prev.filter(task => !task.completed));
    } catch (err) {
      setError('Failed to clear completed tasks. Please try again.');
      console.error('Error clearing completed tasks:', err);
    }
  }, [setTasks]);

  const clearAllTasks = useCallback(async () => {
    try {
      setError(null);
      setTasks([]);
    } catch (err) {
      setError('Failed to clear all tasks. Please try again.');
      console.error('Error clearing all tasks:', err);
    }
  }, [setTasks]);

  const bulkCompleteTasks = useCallback(async (taskIds: number[]) => {
    try {
      setError(null);
      setTasks(prev => prev.map(task => 
        taskIds.includes(task.id) 
          ? { ...task, completed: true }
          : task
      ));
    } catch (err) {
      setError('Failed to complete tasks. Please try again.');
      console.error('Error completing tasks:', err);
    }
  }, [setTasks]);

  return {
    // State
    tasks,
    completedTasks,
    pendingTasks,
    totalTasks,
    completionRate,
    isLoading,
    error,
    
    // Actions
    addTask,
    toggleTask,
    deleteTask,
    generateExcuse,
    clearCompletedTasks,
    clearAllTasks,
    bulkCompleteTasks,
    
    // Utilities
    setError
  };
}
